import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import Menu from "./components/common/Menu";
import Banner from "./components/common/Banner";
import bannerImage from "./assets/home/banner.png";
import Services from "./components/home/Services";
import Card from "./components/common/Card";
import Grid from "./components/common/Grid";
import Footer from "./components/common/Footer";
import ServicesPage from "./pages/Services";
import AboutPage from "./pages/About";
import TeamPage from "./pages/Team";
import ContactPage from "./pages/Contact";
import LoginPage from "./pages/Login";
import PatientDashboard from "./pages/PatientDashboard";
import AppointmentConfirm from "./pages/AppointmentConfirm";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import {
  fetchServiceTypes,
  fetchServices,
  transformServiceData,
} from "./utils/api";
import {
  animateHomeMissionCard,
  animateWhyUsGrid,
  animateFounderCard,
  animateExpertiseCard,
  animateServicesSection,
  refreshScrollTrigger,
} from "./utils/animations";
import physiotherapy from "./assets/home/services/physiotherapy.jpg";
import aboutImage from "./assets/home/about-us/about-us.jpg";
import whiteLogo from "./assets/logo/white-logo.png";
import why1 from "./assets/home/why-us/1.jpg";
import why2 from "./assets/home/why-us/2.jpg";
import why3 from "./assets/home/why-us/3.jpg";
import why4 from "./assets/home/why-us/4.jpg";
import founderImage from "./assets/home/alondra.png";

const App = () => {
  const navigate = useNavigate();
  const [servicesData, setServicesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Refs for animations
  const missionCardRef = useRef(null);
  const whyUsGridRef = useRef(null);
  const founderCardRef = useRef(null);
  const expertiseCardRef = useRef(null);
  const servicesSectionRef = useRef(null);

  const menuItems = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Services", href: "/services" },
    { label: "Team", href: "/team" },
  ];

  const handleBookAppointment = () => {
    window.location.href = "/contact";
  };

  // Fetch services data from API
  useEffect(() => {
    const loadServicesData = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching service types and services from API...");
        const [serviceTypes, services] = await Promise.all([
          fetchServiceTypes(),
          fetchServices(),
        ]);
        console.log("Fetched data:", { serviceTypes, services });
        const transformedData = await transformServiceData(
          serviceTypes,
          services
        );
        console.log("Setting services data:", transformedData);
        setServicesData(transformedData);
      } catch (error) {
        console.error("Failed to load services data:", error);
        // Keep empty array on error - no fallback data
        setServicesData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadServicesData();
  }, []);

  // Animation for Mission Card
  useEffect(() => {
    return animateHomeMissionCard(missionCardRef);
  }, []);

  // Animation for Why Us Grid
  useEffect(() => {
    return animateWhyUsGrid(whyUsGridRef);
  }, []);

  // Animation for Founder Card
  useEffect(() => {
    return animateFounderCard(founderCardRef);
  }, []);

  // Animation for Expertise Card
  useEffect(() => {
    return animateExpertiseCard(expertiseCardRef);
  }, []);

  // Animation for Services Section
  useEffect(() => {
    return animateServicesSection(servicesSectionRef, isLoading, servicesData);
  }, [isLoading, servicesData]);

  // Refresh ScrollTrigger after all animations are set up
  useEffect(() => {
    return refreshScrollTrigger();
  }, []);

  const whyUsData = [
    {
      image: why1,
      text: "Expertise You Can Trust",
    },
    {
      image: why2,
      text: "Innovative Treatments",
    },
    {
      image: why3,
      text: "Tailored for You",
    },
    {
      image: why4,
      text: "Precision & Safety",
    },
  ];

  return (
    <AuthProvider>
      <div style={{ background: "var(--color-secondary)", minHeight: "100vh" }}>
        <Menu
        items={menuItems}
        defaultActive="#home"
        onNavigate={(href) => {
          if (href.startsWith("/")) {
            navigate(href);
            window.scrollTo(0, 0);
          } else if (href.startsWith("#")) {
            window.location.hash = href;
          }
        }}
        actionButton={{
          label: "Book an Appointment",
          onClick: handleBookAppointment,
          backgroundColor: "var(--color-secondary)",
          textColor: "var(--color-primary)",
        }}
        signInButton={{
          label: "Go to Profile",
          variant: "outlined",
          onClick: () => navigate("/login"),
          backgroundColor: "var(--color-primary)",
          textColor: "var(--color-secondary)",
        }}
      />
      <Routes>
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/services"
          element={
            <ServicesPage servicesData={servicesData} isLoading={isLoading} />
          }
        />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/confirm-appointment" element={<AppointmentConfirm />} />
        <Route
          path="/patient-dashboard"
          element={
            <ProtectedRoute>
              <PatientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <>
              <Banner image={bannerImage} />

              <div ref={missionCardRef}>
                <Card
                  image={physiotherapy}
                  imagePosition="right"
                  subtitle="Our Mission? It's simple"
                  title="Transform Your Old Self Into Your New Self"
                  description="Through our advanced face and body care treatments, we combine expertise, innovation, and personalized solutions to help you look and feel your absolute best. We believe that every individual deserves a fresh start—whether it's rejuvenating your skin, sculpting your body, or enhancing your natural beauty."
                  button={{
                    label: "Discover More",
                    onClick: () => {
                      window.location.href = "/about";
                    },
                    variant: "normal",
                    size: "3",
                    backgroundColor: "var(--color-secondary)",
                    textColor: "var(--color-primary)",
                  }}
                  backgroundColor="var(--color-primary)"
                  cardBackgroundColor="var(--color-primary)"
                  textColor="var(--color-secondary)"
                />
              </div>

              <div ref={whyUsGridRef}>
                <Grid
                  items={whyUsData}
                  columns={4}
                  backgroundColor="var(--color-primary)"
                  gap="20px"
                />
              </div>

              <div ref={founderCardRef}>
                <Card
                  image={founderImage}
                  imagePosition="left"
                  subtitle="Founder & CEO"
                  title="Dr. Alondra Azar"
                  description="“My goal is to help you rediscover your glow — the one that’s already within you.”"
                  backgroundColor="var(--color-secondary)"
                  cardBackgroundColor="var(--color-secondary)"
                  textColor="var(--color-primary)"
                  roundImage
                  imageMaxHeight="180px"
                  descriptionEmphasis
                  sectionPadding="2rem"
                  cardPadding="2rem"
                />
              </div>

              <div ref={expertiseCardRef}>
                <Card
                  image={aboutImage}
                  imagePosition="left"
                  subtitle="Our Expertise"
                  title="Advanced Skin Care Solutions"
                  description="At Renova, we combine medical expertise with the art of aesthetics. Our mission is to help you rediscover confidence through advanced facial, body, and hair treatments—delivered with precision, care, and innovation."
                  button={{
                    label: "Meet Our Team",
                    onClick: () => {
                      window.location.href = "/team";
                    },
                    variant: "normal",
                    size: "3",
                    backgroundColor: "var(--color-primary)",
                    textColor: "var(--color-secondary)",
                  }}
                  backgroundColor="var(--color-primary)"
                />
              </div>

              {/* Card with image on the right */}
              {/* <Card
                image={physiotherapy}
                imagePosition="right"
                subtitle="Why Choose Us"
                title="Personalized Care for Every Client"
                description="We believe in tailored treatments that address your unique needs and goals. Our experienced professionals work closely with you to create a customized plan that brings out your best self."
                button={{
                  label: "Book Consultation",
                  onClick: () => console.log("Book Consultation clicked"),
                  variant: "outlined",
                  size: "3",
                }}
                backgroundColor="var(--color-primary)"
              /> */}

              {/* Card without subtitle */}
              {/* <Card
                image={laser}
                imagePosition="left"
                title="Cutting-Edge Technology"
                description="Our clinic is equipped with the most advanced aesthetic technology available. From laser treatments to body sculpting, we invest in the best equipment to ensure optimal results and your complete satisfaction."
                button={{
                  label: "View Technology",
                  onClick: () => console.log("View Technology clicked"),
                  variant: "normal",
                }}
               /> */}

              {/* Grid with 3 columns */}
              {/* <Grid
                items={gridData}
                columns={3}
                backgroundColor="var(--color-primary)"
                gap="20px"
              /> */}

              {/* Grid with 4 columns */}
              {/* <Grid
                items={gridData.slice(0, 4)}
                columns={4}
                backgroundColor="var(--color-secondary)"
                gap="15px"
              /> */}

              {/* Grid with 2 columns */}
              {/* <Grid
                items={gridData.slice(0, 4)}
                columns={2}
                backgroundColor="var(--color-primary)"
                 gap="25px"
               /> */}

              {/* Footer removed from here */}
              <div ref={servicesSectionRef}>
                {isLoading ? (
                  <div style={{ padding: "4rem", textAlign: "center" }}>
                    <p>Loading services...</p>
                  </div>
                ) : servicesData.length > 0 ? (
                  <Services slides={servicesData} />
                ) : (
                  <div style={{ padding: "4rem", textAlign: "center" }}>
                    <p>
                      No services available. Please check the API connection.
                    </p>
                  </div>
                )}
              </div>
            </>
          }
        />
      </Routes>
      <Footer showLogo={true} logo={whiteLogo} menuItems={menuItems} />
      </div>
    </AuthProvider>
  );
};

export default App;
